/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../../IEditor";
import { TOAST_TYPE } from "../../toasts/IToast";
import { LayoutState_Panel } from "../../ui/components/Layout/LayoutState";
import { termsEditorEndpointEditor } from "../../terms/endpointEditor";
import { EndpointEditorEditView } from "./EndpointEditorEdit/EndpointEditorEditView";
import { EndpointEditorCreateDialog } from "./EndpointEditorCreateDialog/EndpointEditorCreateDialog";
import { ErrorTracker } from "@adapptio/lib-components";

// import { CreateExportDialog } from "./CreateActionExportDialog/CreateActionExportDialog";

export function registerCommands(dc: IEditor): void {
	const viewSvc = dc.get("viewService");
	const commandsSvc = dc.get("commandsService");
	const dialogSvc = dc.get("dialogService");
	const toastSvc = dc.get("toastService");

	commandsSvc.register({
		id: "endpointEditor.create.open",
		label: termsEditorEndpointEditor.mainMenu.sectionCreate.action.label,
		args: [
			{
				label: "Base path",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		handler: async (args) => {
			const basePath = args[0] as string;

			const res = await dialogSvc.open(EndpointEditorCreateDialog, "endpointEditor.createAction", {
				basePath
			});

			if (res.resourceId) {
				commandsSvc.execute("resource.open", res.resourceId);
			}

			return res.resourceId;
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.undo",
		label: termsEditorEndpointEditor.commands.undo,
		handler: async () => {
			const endpointEditor = viewSvc.getCurrentView() as EndpointEditorEditView;
			endpointEditor.undo();
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.redo",
		label: termsEditorEndpointEditor.commands.redo,
		handler: async () => {
			const endpointEditor = viewSvc.getCurrentView() as EndpointEditorEditView;
			endpointEditor.redo();
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.toggleLeftPanel",
		label: termsEditorEndpointEditor.commands.toggleLeftPanel,
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				(
					(viewSvc.getCurrentView() as EndpointEditorEditView)
						.getLayout()
						.getById("panelLeft") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.toggleRightPanel",
		label: termsEditorEndpointEditor.commands.toggleRightPanel,
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				(
					(viewSvc.getCurrentView() as EndpointEditorEditView)
						.getLayout()
						.getById("panelRight") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.hideAllPanels",
		label: termsEditorEndpointEditor.commands.hideAllPanels,
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				const layout = (viewSvc.getCurrentView() as EndpointEditorEditView).getLayout();

				(layout.getById("panelLeft") as LayoutState_Panel)?.collapse();
				(layout.getById("panelRight") as LayoutState_Panel)?.collapse();
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.toggleConsole",
		label: termsEditorEndpointEditor.commands.toggleConsole,
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				(
					(viewSvc.getCurrentView() as EndpointEditorEditView)
						.getLayout()
						.getById("console") as LayoutState_Panel
				)?.toggleExpanded();
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.saveResource",
		label: termsEditorEndpointEditor.commands.saveResource,
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				const endpointEditor = viewSvc.getCurrentView() as EndpointEditorEditView;

				if (!endpointEditor.getIsModified() || endpointEditor.isBeingSaved()) {
					return;
				}

				const loadingToast = toastSvc.add({
					type: TOAST_TYPE.LOADING,
					message: termsEditorEndpointEditor.toasts.savingAction,
					isClosable: false
				});

				try {
					await endpointEditor.save();

					toastSvc.add({
						type: TOAST_TYPE.SUCCESS,
						message: termsEditorEndpointEditor.toasts.actionHasBeenSaved,
						timeoutMs: 2500
					});
				} catch (err) {
					const errorTracker = ErrorTracker.getInstance();
					errorTracker.captureException(err);

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: termsEditorEndpointEditor.toasts.failedToSaveAction,
						details: String(err)
					});
				} finally {
					toastSvc.remove(loadingToast);
				}
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.openHistory",
		label: termsEditorEndpointEditor.other.historyNotAvailableYet,
		handler: async () => {
			toastSvc.add({
				type: TOAST_TYPE.INFO,
				message: "History is not implemented yet."
			});
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.saveExecute",
		// @todo Translate
		label: "Save & Execute Action",
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				const view = viewSvc.getCurrentView() as EndpointEditorEditView;

				try {
					await view.save();
					await view.execute();
				} catch (err) {
					const errorTracker = ErrorTracker.getInstance();
					errorTracker.captureException(err);

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: "Failed to save and execute the action.",
						details: String(err)
					});
				}
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.flowEditor.zoomIn",
		// @todo Translate
		label: "Zoom In",
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				const view = viewSvc.getCurrentView() as EndpointEditorEditView;
				view.flowEditorZoomIn();
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.flowEditor.zoomOut",
		// @todo Translate
		label: "Zoom Out",
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				const view = viewSvc.getCurrentView() as EndpointEditorEditView;
				view.flowEditorZoomOut();
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.flowEditor.zoomReset",
		// @todo Translate
		label: "Reset Zoom",
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				const view = viewSvc.getCurrentView() as EndpointEditorEditView;
				view.flowEditorZoomReset();
			}
		}
	});

	commandsSvc.register({
		id: "endpointEditor.edit.flowEditor.zoomFit",
		// @todo Translate
		label: "Fit Nodes into View",
		handler: async () => {
			if (viewSvc.isViewActive("endpointEditor.edit")) {
				const view = viewSvc.getCurrentView() as EndpointEditorEditView;
				view.flowEditorZoomFit();
			}
		}
	});
}
